<template>
  <div class="teaching-photos">
    <div v-if="!readonly" class="header mb-2">
      <el-button
        type="primary"
        size="small"
        icon="el-icon-upload2"
        @click="handleUpload"
      >上传照片
      </el-button>
      <span class="tip ml-2 text-muted">请选择关联课程后上传，单个课程最多保存10张</span>
    </div>

    <el-dialog
      title="上传照片"
      :visible.sync="dialogVisible"
      width="560px"
      :before-close="closeDialog"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form
        ref="uploadForm"
        :model="form"
        :rules="rules"
        label-width="90px"
      >
        <el-form-item label="关联课程" prop="courseId">
          <el-select
            v-model="form.courseId"
            filterable
            remote
            reserve-keyword
            clearable
            placeholder="请输入课程名称搜索"
            :remote-method="searchCourseOptions"
            :loading="courseLoading"
            style="width: 100%;"
            @change="handleCourseChange"
          >
            <el-option
              v-for="item in courseOptions"
              :key="item._id"
              :label="item.name"
              :value="item._id"
            >
              <div class="course-option">
                <span class="course-name">{{ item.name }}</span>
                <span class="course-meta">
                  {{ formatCourseMeta(item) }}
                </span>
              </div>
            </el-option>
          </el-select>
          <div v-if="selectedCourse" class="field-tip">
            已选课程：{{ selectedCourse.name }}，当前已上传 {{ selectedCourse.trainingPhotoCount || 0 }}/10 张
          </div>
        </el-form-item>

        <el-form-item label="上传照片" prop="photos">
          <el-upload
            class="upload-demo"
            list-type="picture-card"
            action=""
            multiple
            accept="image/jpeg,image/png,image/jpg"
            :limit="10"
            :http-request="customUploadRequest"
            :file-list="uploadFileList"
            :auto-upload="true"
            :before-upload="beforeFileUpload"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :on-preview="handleFilePreview"
            :on-exceed="handleUploadExceed"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
          <div class="field-tip">只能上传 jpg/png/jpeg 文件，且不超过 5MB，建议尺寸 16:9</div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="closeDialog()">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitUpload">
          确 定
        </el-button>
      </span>
    </el-dialog>

    <el-dialog :visible.sync="previewVisible" append-to-body>
      <img width="100%" :src="previewUrl" alt="">
    </el-dialog>
  </div>
</template>

<script>
import { ossClient } from "@/plugins/upload";

export default {
  name: "TeachingPhotos",
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    teacherId: {
      type: String,
      default: "",
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dialogVisible: false,
      previewVisible: false,
      previewUrl: '',
      form: {
        courseId: "",
        courseName: "",
        photos: [],
      },
      rules: {
        courseId: [
          { required: true, message: "请选择关联课程", trigger: "change" },
        ],
        photos: [
          { required: true, type: "array", min: 1, message: "请先上传照片", trigger: "change" },
        ],
      },
      uploadFileList: [],
      courseOptions: [],
      courseLoading: false,
      uploadingCount: 0,
      submitting: false,
    };
  },
  computed: {
    selectedCourse() {
      return this.courseOptions.find((item) => item._id === this.form.courseId) || null;
    },
  },
  methods: {
    handleUpload() {
      this.dialogVisible = true;
      this.resetForm();
      this.searchCourseOptions("");
    },
    closeDialog(done) {
      if (done) done();
      this.dialogVisible = false;
      this.resetForm();
    },
    resetForm() {
      this.form = {
        courseId: "",
        courseName: "",
        photos: [],
      };
      this.uploadFileList = [];
      this.uploadingCount = 0;
      this.submitting = false;
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.clearValidate();
      }
    },
    async searchCourseOptions(query) {
      if (!this.teacherId) {
        return;
      }
      this.courseLoading = true;
      try {
        const { data } = await this.$http.get(`teachers/history_courses/${this.teacherId}`, {
          params: {
            keyword: query || "",
          },
        });
        this.courseOptions = data || [];
      } finally {
        this.courseLoading = false;
      }
    },
    handleCourseChange(courseId) {
      const current = this.courseOptions.find((item) => item._id === courseId);
      this.form.courseName = current ? current.name : "";
    },
    formatCourseMeta(item) {
      const parts = [];
      if (item.terminal_name) {
        parts.push(item.terminal_name);
      }
      if (item.latestTime) {
        parts.push(String(item.latestTime).slice(0, 10));
      }
      return parts.join(" | ");
    },
    beforeFileUpload(file) {
      const isImage = ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isImage) {
        this.$message.error("只能上传 jpg/png/jpeg 文件");
        return false;
      }
      if (!isLt5M) {
        this.$message.error("上传图片大小不能超过 5MB");
        return false;
      }
      return true;
    },
    async customUploadRequest(config) {
      const { file } = config;
      this.uploadingCount += 1;
      try {
        if (!this.teacherId) {
            this.$message.error("缺少讲师ID，无法上传");
            config.onError();
            return;
        }

        if (!ossClient.client) {
            await ossClient.getCredential();
        }

        const timestamp = new Date().getTime();
        const path = `teachers/${this.teacherId}/training-photos/${timestamp}-${file.name}`;
        
        const result = await ossClient.client.put(path, file, {
            progress: (p) => {
                if (config.onProgress) {
                    config.onProgress({ percent: p * 100 });
                }
            }
        });

        if (result && result.res && result.res.status === 200) {
            let url = result.url;
            if (url && url.startsWith('http:')) {
                url = url.replace('http:', 'https:');
            }
            config.onSuccess({ url: url, name: file.name });
        } else {
            config.onError(new Error("Upload failed"));
        }
      } catch (error) {
        console.error("OSS Upload Error:", error);
        this.$message.error("上传失败，请重试");
        config.onError(error);
      } finally {
        this.uploadingCount = Math.max(this.uploadingCount - 1, 0);
      }
    },
    handleUploadSuccess(response, file, fileList) {
      file.url = response.url;
      this.uploadFileList = fileList;
      this.form.photos = fileList
        .map((item) => (item.response && item.response.url) || item.url)
        .filter((url) => url);
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.validateField("photos");
      }
    },
    handleUploadRemove(file, fileList) {
      this.uploadFileList = fileList;
      this.form.photos = fileList
        .map((item) => (item.response && item.response.url) || item.url)
        .filter((url) => url);
      if (this.$refs.uploadForm) {
        this.$refs.uploadForm.validateField("photos");
      }
    },
    handleUploadExceed() {
      this.$message.warning("单次最多选择10张照片");
    },
    handleFilePreview(file) {
      this.previewUrl = file.url || (file.response && file.response.url) || "";
      this.previewVisible = true;
    },
    async submitUpload() {
      this.$refs.uploadForm.validate(async (valid) => {
        if (!valid) {
          return;
        }
        if (this.uploadingCount > 0) {
          this.$message.warning("图片仍在上传中，请稍后再提交");
          return;
        }
        const selectedCourse = this.selectedCourse;
        if (selectedCourse && (selectedCourse.trainingPhotoCount || 0) + this.form.photos.length > 10) {
          this.$message.warning(`该课程最多上传10张照片，当前已上传${selectedCourse.trainingPhotoCount || 0}张`);
          return;
        }

        this.submitting = true;
        try {
          const { data } = await this.$http.post("teachers/training_photo/upload", {
            teacherId: this.teacherId,
            courseId: this.form.courseId,
            photos: this.form.photos,
          });

          const updatedList = [...new Set([...(this.value || []), ...this.form.photos])];
          this.$emit("input", updatedList);
          this.$emit("success", data);
          this.$message.success("上传成功");
          this.closeDialog();
        } finally {
          this.submitting = false;
        }
      });
    },
  },
  watch: {
    dialogVisible(val) {
      if (!val) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
.teaching-photos {
  padding: 10px;
}
.text-muted {
  color: #909399;
  font-size: 12px;
}
.ml-2 {
  margin-left: 10px;
}
.mb-2 {
  margin-bottom: 10px;
}
.course-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.course-name {
  color: #303133;
}
.course-meta,
.field-tip {
  color: #909399;
  font-size: 12px;
}
</style>
